import { Workspace } from '@/features/iam/workspaces/Workspace'
import { collection } from '@/infra/collection'
import DataLoader from 'dataloader'
import { type IamApi } from '@/features/iam/api/iam-api'
import { Id } from '@/infra/id'
import { mapWorkspaceResourceToProps } from '@/features/iam/workspaces/workspace-mappers'
import type { PreselectedWorkspace } from '@/features/iam/workspaces/PreselectedWorkspaceProvider'
import { type WorkspaceMembership } from '@/features/iam/workspaces/WorkspaceMembership'
import { type GetCurrentUserFn, type PrimeWorkspaceProps } from '@/features/iam'
import { WorkspaceBranding } from '@/features/iam/workspaces/workspace-branding'

/**
 * Manages state related to workspaces.
 */
export class WorkspaceStore {
  /**
   * Collection of in-memory workspaces.
   */
  readonly workspaces = collection({
    getDataId: (w) => w.id,
    getModelId: (w) => w.id,
    create: () => new Workspace(),
  })

  /**
   * Workspace data-loader.
   */
  private readonly _workspaceLoader = new DataLoader<string, Workspace | null>(
    this._fetchWorkspaceCore.bind(this),
  )

  constructor(
    private readonly _api: IamApi,
    private readonly _getCurrentUser: GetCurrentUserFn,
  ) {}

  /**
   * Primes a workspace.
   *
   * @param props
   */
  primeWorkspace(props: PrimeWorkspaceProps): Workspace {
    const workspace = this.workspaces.set({
      id: props.id,
    })

    void this.fetchWorkspace(props.id)
    return workspace
  }

  /**
   * Fetches the preselected workspace if there is one.
   */
  async fetchPreselectedWorkspace(
    preselection: PreselectedWorkspace,
  ): Promise<Workspace | null> {
    switch (preselection.type) {
      case 'Url': {
        // Hydrate using the included resource and trigger a background fetch.
        const workspace = this.workspaces.set(
          mapWorkspaceResourceToProps(preselection.workspaceResource),
        )
        void this.fetchWorkspace(workspace.id)
        return workspace
      }

      case 'Id':
        return await this.fetchWorkspace(preselection.workspaceId)
    }
  }

  /**
   * Fetches a workspace by ID.
   */
  async fetchWorkspace(id: Id) {
    return this._workspaceLoader.load(Id.assert(id))
  }

  /**
   * Fetches workspace memberships for the current user.
   */
  async listWorkspaceMemberships(
    signal?: AbortSignal,
  ): Promise<WorkspaceMembership[]> {
    const resources = await this._api.users.getUserWorkspaceMemberships(
      Id.assert(this._getCurrentUser().id),
      signal,
    )
    return resources.map(
      (resource): WorkspaceMembership => ({
        id: resource.workspace.id,
        name: resource.workspace.name,
        slug: resource.workspace.slug,
        iconUrl:
          resource.workspace.branding?.logos?.icon ||
          WorkspaceBranding.default.logos.icon,
        invertedIconUrl: resource.workspace.branding?.logos?.icon_white || null,
        primaryColor:
          resource.workspace.branding?.colors?.primary ||
          WorkspaceBranding.default.colors.primary,
      }),
    )
  }

  /**
   * Fetches multiple workspaces
   */
  private async _fetchWorkspaceCore(ids: readonly string[]) {
    const resources = await this._api.workspaces.getMany(ids)
    return this.workspaces.set(
      resources.map((w) => (w ? mapWorkspaceResourceToProps(w) : w)),
    )
  }
}
